//@flow

import type { ElementRef } from "react";
import { createRef, useState } from "react";
import React from "react";
import ReactJWPlayer from "react-jw-player";

import VolumeOffIcon from "@/images/VolumeOff.inline.svg";
import VolumeUpIcon from "@/images/VolumeUp.inline.svg";

type VideoRef = { current: ElementRef<string> | null };

export type VideoPlayerAspectRatio =
    | "16:9"
    | "4:3"
    | "1:1"
    | "12:5"
    | "21:9"
    | "480:259"
    | "120:109"
    | "545:294";

type Props = {
    /**
     * A unique Id for the player instance. Used to distinguish the container
     * divs.
     */
    playerId: string,

    /**
     * Link to a valid JW Player script.
     */
    playerScript: string,

    /**
     * Link to a valid JW Player video file.
     */
    file: string,

    /**
     * Initial muted state of the video player, true by default.
     */
    muted?: boolean,

    /**
     * Display video player controls, "sound-only" by default. "true" for all
     * controls, "false" for none and "sound-only" for only the sound control.
     */
    controls?: boolean | "sound-only",

    /**
     * Keep video playing when it's finished, true by default.
     */
    repeat?: boolean,

    /**
     * Automatically start the video, "viewable" by default. Viewable will start
     * the video automatically when the player is more than 50% visible in the
     * active tab. "true" will always autostart the video.
     */
    autostart?: 'viewable' | boolean,

    /**
     * Automatically pause the video when it is no longer visible.
     */
    autoPause?: boolean,

    /**
     * Pause (or play) the video when the player is clicked.
     */
    pauseOnClick?: boolean,

    /**
     * The aspect ratio of the video.
     */
    aspectRatio: VideoPlayerAspectRatio,

    /**
     * URL to a poster image to display before and after video plays.
     */
    image?: ?string,

    /**
     * Resize images and video to fit player dimensions.
     */
    stretching?: 'uniform' | 'exactfit' | 'fill' | 'none',

    /**
     * Allow multiple video's from playing at the same time, defaults to false.
     */
    allowMultipleVideoPlayback?: boolean,
};

type JWTimeEvent = {
    type: "time",
    position: number,
    duration: number,
    seekRange: {
        start: number,
        end: number,
    },
};

// https://docs.jwplayer.com/players/reference/jwplayersetconfig
type JWPlayerProps = {
    controls: boolean,
    repeat: boolean,
    autoPause?: {
        pauseAds?: boolean,
        visibility?: boolean,
    },
    autostart: 'viewable' | boolean,
    stretching: 'uniform' | 'exactfit' | 'fill' | 'none',
    aspectratio?: string,
};

const VideoPlayer = (props: Props): React$Element<"div"> => {
    const ref = createRef<VideoRef>();

    const [state, setState] = useState<{
        muted: boolean,
        controls: string,
        repeat: boolean,
        autostart: string,
        autoPause: boolean,
        pauseOnClick: boolean,
        stretching: 'uniform' | 'exactfit' | 'fill' | 'none',
        image?: string,
        allowMultipleVideoPlayback: boolean,
        aspectRatio: VideoPlayerAspectRatio,
        playing: boolean,
        showImage: boolean,
    }>({
        muted: props.muted !== undefined ? props.muted : true,
        controls: "sound-only",
        repeat: props.repeat !== undefined ? props.repeat : true,
        autostart: "viewable",
        autoPause: props.autoPause !== undefined ? props.autoPause : true,
        pauseOnClick: props.pauseOnClick !== undefined ? props.pauseOnClick : true,
        stretching: props.stretching ?? "uniform",
        image: undefined,
        allowMultipleVideoPlayback: props.allowMultipleVideoPlayback !== undefined ? props.allowMultipleVideoPlayback : false,
        aspectRatio: props.aspectRatio,
        playing: props.autostart === "true",
        showImage: false,
    });

    /**
     * Toggles the muted state.
     */
    const toggleMuted = () => {
        setState(
            state => ({
                ...state,
                muted: !state.muted,
            }),
        );
    }

    /**
     * Play or pause the video.
     */
    const handlePlayerClickedEvent = () => {
        if (!props.pauseOnClick) {
            return;
        }
        
        setState(
            state => ({
                ...state,
                playing: !state.playing,
            }),
        );
    }

    const handleVideoEnd = () => {
        if (props.image !== undefined && props.repeat === false) {
            setState(
                state => ({
                    ...state,
                    showImage: true,
                }),
            );
        }
    }
      
    const onTime: ((event: JWTimeEvent) => void) = (event: JWTimeEvent) => {
        if (props.repeat) {
            if (Math.floor(event.position) >= Math.floor(event.duration)) {
                const player = getPlayer();
                player.seek(event.seekRange.start);
                player.play();
            }
        }
    };
      
    /**
     * Return the custom properties specific for the JW Player.
     */
    const getCustomProps = (): JWPlayerProps => {
        const jWPlayerProps: JWPlayerProps = {
            controls: props.controls === true,
            repeat: false,
            autostart: props.autostart !== undefined ? props.autostart : "viewable",
            stretching: props.stretching ?? "uniform",
            aspectratio: props.aspectRatio,
            autoPause: {
                visibility: props.autoPause,
            },
        };

        // The "aspectratio" property shouldn't exists if it isn't provided.
        // Otherwise JW Player will try to set the aspect ratio to an incorrect
        // value even if the value is null or undefined.

        if (props.aspectRatio === undefined) {
            delete jWPlayerProps.aspectratio;
        }
        
        return jWPlayerProps;
    };
    
    /**
     * Get the JW Player instance.
     */
    const getPlayer = () => window.jwplayer(props.playerId)
      
    /**
     * Finds all the registered JW player instances on the current page.
     */
    const getPlayers = () => {
        let index = 0;
        let player = window.jwplayer(index);
        const players = [];
        while (player.id != null) {
            players.push(player);
            index += 1;
            player = window.jwplayer(index);
        }
        return players;
    }
      
    /**
     * Pauses all registered JW players with a different ID.
     */
    const onPlayOrResume: (() => void) = () => {
        if (props.allowMultipleVideoPlayback) {
            return;
        }
        
        getPlayers().filter(player => player.id !== props.playerId).filter(
            player => ["playing"].includes(player.getState()),
        ).forEach(
            player => {
                player.pause();
            },
        );
    };

    return (
        <div
            className={"video position-relative"}
            data-aspect-ratio={props.aspectRatio ?? "unknown"}
        >
            <div className="position-absolute w-100 h-100">
                <div
                    style={{
                        backgroundImage: `url('${props.image || ""}')`,
                    }}
                    className={"visual h-100 position-absolute top left"}
                />
                
                <div className={state.showImage ? "hidden" : ""}>
                    <ReactJWPlayer
                        ref={ref}
                        playerId={props.playerId}
                        playerScript={props.playerScript}
                        file={props.file}
                        isMuted={state.muted}
                        customProps={getCustomProps()}
                        useMultiplePlayerScripts={true}
                        onClick={() => handlePlayerClickedEvent()}
                        onOneHundredPercent={() => handleVideoEnd()}
                        onPlay={onPlayOrResume.bind(this)}
                        onResume={onPlayOrResume.bind(this)}
                        onTime={onTime.bind(this)}
                    />
                </div>
                
                {props.controls === "sound-only" &&
                    <div className="controls">
                        <div className="mute">
                            <span className="text">
                                {" "}
                                Sound is {state.muted ? "off" : "on"}
                            </span>
                            
                            <button onClick={() => toggleMuted()}>
                                {state.muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </button>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

export default VideoPlayer;
